import {
  composeOfacLegalName,
  composeOfacPlace,
  ofacNationalIdTypes,
  resolvedOfacPart,
  type OfacNameStyle,
} from './ofacIntakeCatalog';

export function ofacCreateListNameRequiredMessage(
  language: 'en' | 'es',
  hireLocation: string,
): string {
  const city = String(hireLocation || '').trim();
  if (city) {
    return language === 'es'
      ? `Escribe el nombre de la lista en este recuadro. "${city}" en Ubicación de trabajo es el sitio de la persona. La lista también puede llamarse como una ciudad.`
      : `Type the list name in this box. "${city}" in Work location is the person's site. The list can also be named after a city.`;
  }
  return language === 'es'
    ? 'El nombre de la lista es obligatorio. Puede ser una ciudad, un equipo o un año.'
    : 'Type a list name in New list name, then Create. A city, team, or year is fine.';
}

export function ofacGatewaySessionRequiredMessage(language: 'en' | 'es'): string {
  return language === 'es'
    ? 'Esta sesión local no tiene token del gateway. Cierra sesión e inicia con admin@admin.com / password.'
    : 'This local session has no gateway token. Sign out and sign in with admin@admin.com / password.';
}

export function ofacCompanyRequiredMessage(language: 'en' | 'es'): string {
  return language === 'es'
    ? 'Elige o crea una empresa antes de añadir, editar o borrar listas blancas.'
    : 'Select or create a company before adding, editing, or deleting whitelists.';
}

export function ofacCompanyNameRequiredMessage(language: 'en' | 'es'): string {
  return language === 'es'
    ? 'El nombre de la empresa es obligatorio.'
    : 'Company name is required.';
}

export const OFAC_WHITELIST_ROSTER_RELATIONSHIPS = [
  'customer',
  'vendor',
  'ubo_director',
  'employee',
  'contractor',
  'associate',
] as const;

export type OfacWhitelistRosterRelationship = (typeof OFAC_WHITELIST_ROSTER_RELATIONSHIPS)[number];

export type OfacWhitelistIntakeKind = 'person' | 'company';

export const OFAC_WHITELIST_PERSON_ROLES = [
  'ubo_director',
  'employee',
  'contractor',
  'associate',
] as const satisfies readonly OfacWhitelistRosterRelationship[];

export const OFAC_WHITELIST_COMPANY_ROLES = [
  'customer',
  'vendor',
] as const satisfies readonly OfacWhitelistRosterRelationship[];

export function isOfacWhitelistRosterRelationship(value: string): value is OfacWhitelistRosterRelationship {
  return (OFAC_WHITELIST_ROSTER_RELATIONSHIPS as readonly string[]).includes(value);
}

export function ofacWhitelistIntakeKindForRelationship(value: string): OfacWhitelistIntakeKind {
  return (OFAC_WHITELIST_PERSON_ROLES as readonly string[]).includes(value) ? 'person' : 'company';
}

export function ofacWhitelistRolesForKind(
  kind: OfacWhitelistIntakeKind,
): readonly OfacWhitelistRosterRelationship[] {
  return kind === 'person' ? OFAC_WHITELIST_PERSON_ROLES : OFAC_WHITELIST_COMPANY_ROLES;
}

export function ofacWhitelistDefaultRole(kind: OfacWhitelistIntakeKind): OfacWhitelistRosterRelationship {
  return kind === 'person' ? 'contractor' : 'customer';
}

export const OFAC_HIRE_PATHWAYS = ['venezuela_caracas', 'generic_hire'] as const;

export function isOfacHirePathway(value?: string | null): boolean {
  const pathway = String(value || '').trim();
  return (OFAC_HIRE_PATHWAYS as readonly string[]).includes(pathway);
}

export const OFAC_REVIEW_CYCLE_DAYS = [90, 180, 365] as const;

export type OfacReviewCycleDays = (typeof OFAC_REVIEW_CYCLE_DAYS)[number];

export function normalizeOfacReviewCycleDays(value: unknown): OfacReviewCycleDays {
  const days = Number(value);
  if ((OFAC_REVIEW_CYCLE_DAYS as readonly number[]).includes(days)) {
    return days as OfacReviewCycleDays;
  }
  return 365;
}

export function ofacOwnerRequiredMessage(language: 'en' | 'es'): string {
  return language === 'es'
    ? 'Escribe quién es dueño de esta entrada (nombre o correo).'
    : 'Name the owner of this roster entry (person or desk).';
}

export const OFAC_INTAKE_MAX_ROWS = 8;

export const OFAC_ADDRESS_KINDS = [
  { value: 'home', labelEn: 'Home', labelEs: 'Domicilio' },
  { value: 'work', labelEn: 'Work', labelEs: 'Trabajo' },
  { value: 'previous', labelEn: 'Previous', labelEs: 'Anterior' },
] as const;

const OFAC_COMPANY_IDENTIFIER_TYPES = new Set([
  'tax_id',
  'rif',
  'rfc',
  'nit',
  'nif',
  'ein',
  'cnpj',
  'ruc',
  'other',
]);

export function ofacWhitelistAddressKinds(kind: OfacWhitelistIntakeKind) {
  return kind === 'company'
    ? OFAC_ADDRESS_KINDS.filter((row) => row.value !== 'home')
    : OFAC_ADDRESS_KINDS;
}

export function ofacWhitelistIdentifierTypes(kind: OfacWhitelistIntakeKind, country: string) {
  const types = ofacNationalIdTypes(country);
  if (kind === 'person') {
    return types;
  }
  const filtered = types.filter((row) => OFAC_COMPANY_IDENTIFIER_TYPES.has(row.value));
  if (filtered.some((row) => row.value !== 'other')) {
    return filtered;
  }
  return [
    { value: 'tax_id', labelEn: 'Tax ID', labelEs: 'ID fiscal' },
    ...filtered.filter((row) => row.value === 'other'),
  ];
}

export const OFAC_FAMILY_RELATIONS = [
  { value: 'spouse', labelEn: 'Spouse', labelEs: 'Cónyuge' },
  { value: 'parent', labelEn: 'Parent', labelEs: 'Padre/madre' },
  { value: 'sibling', labelEn: 'Sibling', labelEs: 'Hermano/a' },
  { value: 'child', labelEn: 'Child', labelEs: 'Hijo/a' },
  { value: 'adult_child', labelEn: 'Adult child', labelEs: 'Hijo/a adulto' },
  { value: 'associate', labelEn: 'Associate', labelEs: 'Asociado' },
  { value: 'other', labelEn: 'Other', labelEs: 'Otro' },
] as const;

export type OfacIntakeAddress = {
  kind: string;
  line: string;
  line2?: string;
  neighborhood?: string;
  city: string;
  region: string;
  county?: string;
  postalCode: string;
  country: string;
  otherCountry?: string;
  otherRegion?: string;
  otherCounty?: string;
  otherCity?: string;
};

export type OfacIntakeFamilyMember = {
  name: string;
  relation: string;
};

export type OfacIntakeEmployer = {
  name: string;
  location: string;
};

export type OfacWhitelistIntakeFields = {
  relationshipType: string;
  identifier?: string;
  identifierCountry?: string;
  identifierType?: string;
  passport?: string;
  aliases?: string;
  birthdate?: string;
  placeOfBirth?: string;
  nationality?: string;
  country?: string;
  addressLine?: string;
  addressCity?: string;
  addressRegion?: string;
  postalCode?: string;
  hireLocation?: string;
  spouseName?: string;
  familyNames?: string;
  notes?: string;
  owner?: string;
  recertifyDays?: number;
  nameStyle?: OfacNameStyle | string;
  givenName?: string;
  givenName2?: string;
  surname?: string;
  surname2?: string;
  birthCountry?: string;
  birthRegion?: string;
  birthCounty?: string;
  birthCity?: string;
  otherBirthCountry?: string;
  otherBirthRegion?: string;
  otherBirthCounty?: string;
  otherBirthCity?: string;
  addresses?: OfacIntakeAddress[];
  familyMembers?: OfacIntakeFamilyMember[];
  employers?: OfacIntakeEmployer[];
};

export function emptyOfacIntakeAddress(kind = 'home'): OfacIntakeAddress {
  return {
    kind,
    line: '',
    line2: '',
    neighborhood: '',
    city: '',
    region: '',
    county: '',
    postalCode: '',
    country: '',
    otherCountry: '',
    otherRegion: '',
    otherCounty: '',
    otherCity: '',
  };
}

export function emptyOfacIntakeFamilyMember(): OfacIntakeFamilyMember {
  return { name: '', relation: 'spouse' };
}

export function emptyOfacIntakeEmployer(): OfacIntakeEmployer {
  return { name: '', location: '' };
}

export function appendOfacIntakeRow<T>(rows: T[] | undefined, empty: T, max = OFAC_INTAKE_MAX_ROWS): T[] {
  const current = Array.isArray(rows) && rows.length ? rows : [];
  if (current.length >= max) {
    return current;
  }
  return [...current, empty];
}

export function removeOfacIntakeRow<T>(rows: T[] | undefined, index: number, empty: T): T[] {
  const next = (Array.isArray(rows) ? rows : []).filter((_, rowIndex) => rowIndex !== index);
  return next.length ? next : [empty];
}

export function patchOfacIntakeRow<T extends object>(
  rows: T[] | undefined,
  index: number,
  patch: Partial<T>,
  empty: T,
): T[] {
  const current = Array.isArray(rows) && rows.length ? [...rows] : [empty];
  while (current.length <= index) {
    current.push(empty);
  }
  current[index] = { ...current[index], ...patch };
  return current;
}

export function firstOfacHireLocation(form: {
  hireLocation?: string;
  employers?: Array<{ location?: string }>;
}): string {
  const fromEmployers = (form.employers || [])
    .map((row) => String(row?.location || '').trim())
    .find(Boolean);
  return String(fromEmployers || form.hireLocation || '').trim();
}

function flattenOfacIntake(input: OfacWhitelistIntakeFields): OfacWhitelistIntakeFields {
  const address = (input.addresses || []).find((row) => (
    [row.line, row.line2, row.neighborhood, row.city, row.region, row.county, row.postalCode, row.country]
      .some((part) => String(part || '').trim())
  ));
  const family = (input.familyMembers || []).filter((row) => String(row.name || '').trim());
  const spouse = family.find((row) => row.relation === 'spouse');
  return {
    ...input,
    addressLine: input.addressLine || address?.line,
    addressCity: input.addressCity || address?.city,
    addressRegion: input.addressRegion || address?.region,
    postalCode: input.postalCode || address?.postalCode,
    country: input.country || resolvedOfacPart(address?.country, address?.otherCountry) || address?.country,
    hireLocation: firstOfacHireLocation(input) || input.hireLocation,
    spouseName: input.spouseName || spouse?.name,
    familyNames: input.familyNames || family.map((row) => String(row.name).trim()).join('; '),
    placeOfBirth: composeOfacIntakePlace(input) || input.placeOfBirth,
  };
}

export function emptyOfacWhitelistForm(relationshipType = 'contractor') {
  return {
    entityName: '',
    relationshipType,
    aliases: '',
    identifier: '',
    identifierCountry: '',
    identifierType: '',
    passport: '',
    birthdate: '',
    placeOfBirth: '',
    nationality: '',
    country: '',
    addressLine: '',
    addressCity: '',
    addressRegion: '',
    postalCode: '',
    hireLocation: '',
    spouseName: '',
    familyNames: '',
    notes: '',
    owner: '',
    recertifyDays: 365 as OfacReviewCycleDays,
    nameStyle: 'hispanic' as OfacNameStyle,
    givenName: '',
    givenName2: '',
    surname: '',
    surname2: '',
    birthCountry: '',
    birthRegion: '',
    birthCounty: '',
    birthCity: '',
    otherBirthCountry: '',
    otherBirthRegion: '',
    otherBirthCounty: '',
    otherBirthCity: '',
    addresses: [] as OfacIntakeAddress[],
    familyMembers: [] as OfacIntakeFamilyMember[],
    employers: [] as OfacIntakeEmployer[],
  };
}

export function applyOfacWhitelistIntakeKind<T extends ReturnType<typeof emptyOfacWhitelistForm>>(
  current: T,
  kind: OfacWhitelistIntakeKind,
): T {
  const relationshipType = ofacWhitelistDefaultRole(kind);
  return {
    ...emptyOfacWhitelistForm(relationshipType),
    relationshipType,
    owner: current.owner,
    recertifyDays: current.recertifyDays,
  };
}

export function composeOfacIntakeLegalName(input: Pick<
  OfacWhitelistIntakeFields,
  'nameStyle' | 'givenName' | 'givenName2' | 'surname' | 'surname2'
> & { entityName?: string }): string {
  return composeOfacLegalName({
    style: input.nameStyle,
    givenName: input.givenName,
    givenName2: input.givenName2,
    surname: input.surname,
    surname2: input.surname2,
  }) || String(input.entityName || '').trim();
}

export function syncOfacIntakeLegalName<T extends OfacWhitelistIntakeFields & { entityName?: string }>(
  form: T,
): T {
  const composed = composeOfacIntakeLegalName(form);
  return composed ? { ...form, entityName: composed } : form;
}

export function composeOfacIntakePlace(input: OfacWhitelistIntakeFields): string {
  return composeOfacPlace({
    country: input.birthCountry,
    otherCountry: input.otherBirthCountry,
    region: input.birthRegion,
    otherRegion: input.otherBirthRegion,
    county: input.birthCounty,
    otherCounty: input.otherBirthCounty,
    city: input.birthCity,
    otherCity: input.otherBirthCity,
  }) || String(input.placeOfBirth || '').trim();
}

function putIdentifier(
  identifiers: Record<string, string | number>,
  key: string,
  value?: string | number | null,
) {
  if (value == null) {
    return;
  }
  if (typeof value === 'number') {
    identifiers[key] = value;
    return;
  }
  const text = String(value).trim();
  if (text) {
    identifiers[key] = text;
  }
}

export function buildOfacWhitelistIdentifiers(
  input: OfacWhitelistIntakeFields,
): Record<string, string | number> {
  const identifiers: Record<string, string | number> = {};
  input = flattenOfacIntake(input);
  const identifier = String(input.identifier || '').trim();
  if (identifier) {
    const key = input.relationshipType === 'vessel'
      ? 'imo'
      : input.relationshipType === 'aircraft'
        ? 'tail_number'
        : 'id';
    identifiers[key] = identifier;
  }
  putIdentifier(identifiers, 'passport', input.passport);
  putIdentifier(identifiers, 'aliases', input.aliases);
  putIdentifier(identifiers, 'given_name', input.givenName);
  putIdentifier(identifiers, 'given_name2', input.givenName2);
  putIdentifier(identifiers, 'surname', input.surname);
  putIdentifier(identifiers, 'surname2', input.surname2);
  putIdentifier(identifiers, 'name_style', input.nameStyle);
  putIdentifier(identifiers, 'national_id_country', input.identifierCountry);
  putIdentifier(identifiers, 'national_id_type', input.identifierType);
  putIdentifier(identifiers, 'national_id', input.identifier);
  putIdentifier(identifiers, 'birthdate', input.birthdate);
  putIdentifier(identifiers, 'birth_country', resolvedOfacPart(input.birthCountry, input.otherBirthCountry));
  putIdentifier(identifiers, 'birth_region', resolvedOfacPart(input.birthRegion, input.otherBirthRegion));
  putIdentifier(identifiers, 'birth_county', resolvedOfacPart(input.birthCounty, input.otherBirthCounty));
  putIdentifier(identifiers, 'birth_city', resolvedOfacPart(input.birthCity, input.otherBirthCity));
  putIdentifier(identifiers, 'place_of_birth', composeOfacIntakePlace(input) || input.placeOfBirth);
  putIdentifier(identifiers, 'nationality', input.nationality);
  putIdentifier(identifiers, 'country', input.country);
  putIdentifier(identifiers, 'address_line', input.addressLine);
  putIdentifier(identifiers, 'address_city', input.addressCity);
  putIdentifier(identifiers, 'address_region', input.addressRegion);
  putIdentifier(identifiers, 'postal_code', input.postalCode);
  putIdentifier(identifiers, 'hire_location', input.hireLocation);
  const addressRows = (input.addresses || []).filter((row) => (
    [row.line, row.line2, row.neighborhood, row.city, row.region, row.county, row.postalCode, row.country]
      .some((part) => String(part || '').trim())
  ));
  if (addressRows.length) {
    identifiers.addresses = JSON.stringify(addressRows);
  }
  putIdentifier(identifiers, 'spouse_name', input.spouseName);
  putIdentifier(identifiers, 'family_names', input.familyNames);
  const familyRows = (input.familyMembers || []).filter((row) => String(row.name || '').trim());
  if (familyRows.length) {
    identifiers.family_members = JSON.stringify(familyRows);
  }
  putIdentifier(identifiers, 'notes', input.notes);
  putIdentifier(identifiers, 'whitelist_owner', input.owner);
  if (input.recertifyDays != null) {
    identifiers.hire_recertify_days = normalizeOfacReviewCycleDays(input.recertifyDays);
  }
  return identifiers;
}

export function buildOfacFamilyMemberIdentifiers(input: {
  relation: string;
  relatedTo: string;
  owner: string;
  recertifyDays?: number;
}): Record<string, string | number> {
  const identifiers: Record<string, string | number> = {};
  putIdentifier(identifiers, 'family_relation', input.relation);
  putIdentifier(identifiers, 'related_to', input.relatedTo);
  putIdentifier(identifiers, 'whitelist_owner', input.owner);
  if (input.recertifyDays != null) {
    identifiers.hire_recertify_days = normalizeOfacReviewCycleDays(input.recertifyDays);
  }
  return identifiers;
}

export function ofacWhitelistRecordkeepingCsv(input: {
  companyName: string;
  whitelistName: string;
  subjects: Array<{
    entity_name: string;
    relationship_type: string;
    entity_type?: string;
    current_status?: string | null;
    hire_clearance?: {
      last_validated_at?: string | null;
      last_validated_status?: string | null;
      recertify_due?: boolean;
      recertify_days?: number;
      owner?: string | null;
    } | null;
  }>;
  pairs: Array<{
    subject_id?: number;
    list_entry_id: string;
    list_entry_name?: string | null;
    status: string;
    approved_at?: string | null;
    recertified_at?: string | null;
    subject_name?: string;
  }>;
}): string {
  const escape = (value: unknown): string => {
    const raw = String(value ?? '');
    if (/[",\n]/.test(raw)) {
      return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
  };
  const header = [
    'record_type',
    'company',
    'whitelist',
    'name',
    'role',
    'owner',
    'recertify_days',
    'entity_type',
    'status',
    'last_validated_at',
    'last_validated_status',
    'recertify_due',
    'list_entry_id',
    'list_entry_name',
    'pair_status',
    'approved_at',
    'recertified_at',
  ];
  const rows = [header.join(',')];
  for (const subject of input.subjects) {
    rows.push([
      'subject',
      escape(input.companyName),
      escape(input.whitelistName),
      escape(subject.entity_name),
      escape(subject.relationship_type),
      escape(subject.hire_clearance?.owner || ''),
      escape(subject.hire_clearance?.recertify_days || ''),
      escape(subject.entity_type || ''),
      escape(subject.current_status || ''),
      escape(subject.hire_clearance?.last_validated_at || ''),
      escape(subject.hire_clearance?.last_validated_status || ''),
      escape(subject.hire_clearance?.recertify_due ? 'yes' : 'no'),
      '',
      '',
      '',
      '',
      '',
    ].join(','));
  }
  for (const pair of input.pairs) {
    rows.push([
      'pair',
      escape(input.companyName),
      escape(input.whitelistName),
      escape(pair.subject_name || ''),
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      escape(pair.list_entry_id),
      escape(pair.list_entry_name || ''),
      escape(pair.status),
      escape(pair.approved_at || ''),
      escape(pair.recertified_at || ''),
    ].join(','));
  }
  return `${rows.join('\n')}\n`;
}

export function ofacDirectoryCompanyRejectedMessage(language: 'en' | 'es'): string {
  return language === 'es'
    ? 'Ese nombre es de plataforma, producto o demo. Usa el cliente que vas a autorizar.'
    : 'That name is a platform, product, or demo tenant. Use the customer you will screen.';
}

const OFAC_DIRECTORY_EXCLUDED_NAMES = new Set([
  'nextcompli',
  'demo customer co',
  'complicore',
  'keycloak',
  'admin',
  'essentials e2e tenant',
  'essentials denied tenant',
  'essentials pilot',
  'firstfridays',
  'gossled ofac demo',
  'e2e platform admin',
  'complicore e2e tenant',
]);

const OFAC_DIRECTORY_EXCLUDED_TOKENS = new Set([
  'nextcompli',
  'complicore',
  'keycloak',
  'firstfridays',
  'e2e',
]);

const OFAC_DIRECTORY_EXCLUDED_PHRASES = [
  'platform admin',
  'ofac demo',
];

const OFAC_DIRECTORY_EXCLUDED_PREFIXES = [
  'essentials onboard api',
  'essentials ',
  'stripe ',
  'stripe-',
  'e2e ',
];

function normalizeOfacDirectoryName(name: string): string {
  return String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function isOfacCustomerDirectoryCompany(
  name: string,
  groupType?: string | null,
): boolean {
  if (String(groupType || '').trim().toLowerCase() === 'team') {
    return false;
  }
  const key = normalizeOfacDirectoryName(name);
  if (!key || OFAC_DIRECTORY_EXCLUDED_NAMES.has(key)) {
    return false;
  }
  if (OFAC_DIRECTORY_EXCLUDED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
    return false;
  }
  if (OFAC_DIRECTORY_EXCLUDED_PHRASES.some((phrase) => key.includes(phrase))) {
    return false;
  }
  const tokens = new Set(key.replace(/[-_]/g, ' ').split(/\s+/).filter(Boolean));
  return ![...tokens].some((token) => OFAC_DIRECTORY_EXCLUDED_TOKENS.has(token));
}
